/**
 * EvidenceDrawer — per-checklist-point evidence viewer.
 *
 * Opens a right-side slide-out (via the existing Sheet primitive) showing
 * the auditor exactly what data the rule looked at for a specific
 * checklist point. Lets the auditor verify every check, not just trust it.
 *
 * Phase 1 (this commit):
 *   - Drawer infrastructure + 13 LOS-data evidence renderers (points
 *     2-10, 12-16) fully implemented.
 *   - 4 PDF-evidence points (1, 11, 17) render a Phase-2 placeholder
 *     that lists the relevant PDF file names but cannot embed them yet
 *     (no presigned-GET URL endpoint).
 *
 * Phase 2 (next session): add backend endpoint for presigned-GET URLs
 * and a PdfEmbed component, then swap the placeholders for live PDFs.
 */

import { useState, useEffect, type ReactNode } from 'react';
import {
  CheckCircle2, XCircle, MinusCircle, ShieldCheck, FileText, ExternalLink,
  AlertTriangle, Users, Calendar, Building2, ClipboardCheck,
} from 'lucide-react';
import { Sheet } from '@/acc-ui/primitives/Sheet';
import { Badge } from '@/acc-ui/primitives/Badge';
import { PdfEmbed } from './PdfEmbed';
import type {
  FindingDTO, LosApplicationDataDTO, LoanFileParsingView, BankingVerificationDTO,
} from '@loanaudit/shared';

// -------------------------------------------------------------------------
// Public types — what the parent grid passes in
// -------------------------------------------------------------------------

export type RowStatus =
  | 'compliant' | 'deviation_justified' | 'violated' | 'inconclusive' | 'no_los';

export interface EvidenceDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Loan file the evidence belongs to — used by PdfEmbed for presigned URLs. */
  loanFileId: string;
  /** Checklist point number 1-17. Selects the renderer. */
  point: number | null;
  /** Short label like "Hunter Verification" — shown in the drawer title. */
  label: string;
  /** Status of the row, drives the verdict header in the drawer. */
  status: RowStatus;
  /** The finding produced by the rule (if any). Null for clean-passed rows. */
  finding: FindingDTO | null;
  /** LOS data attached to the loan file, if any. */
  losData: LosApplicationDataDTO | null;
  /** Parsed documents on the loan file — name + type + id. */
  documents: Array<{
    id: string;
    originalFileName: string;
    documentType: string;
  }>;
  /** LOS-screen path string for the row, shown in every drawer. */
  losPath: string;
  /** Parsing view (from parsingApi.getLoanFileView) — provides per-field
   *  page numbers used by Point 11 for "→ Page N" jump buttons. Optional;
   *  evidence renderers fall back gracefully when missing. */
  parsingView?: LoanFileParsingView | null;
  /**
   * Latest completed banking verification for the loan file (Chunk 2).
   * Rendered in points 8-10 alongside LOS novelCheckBanking data.
   * Optional — renderers fall back to LOS flags when null.
   */
  bankingVerification?: BankingVerificationDTO | null;
}

// -------------------------------------------------------------------------
// Verdict header — same look across all 17 renderers
// -------------------------------------------------------------------------

const STATUS_META: Record<RowStatus, {
  label: string;
  icon: ReactNode;
  cls: string;
  tone: 'success' | 'info' | 'danger' | 'warning' | 'neutral';
}> = {
  compliant: {
    label: 'Passed',
    icon: <CheckCircle2 className="w-4 h-4 text-emerald-700" />,
    cls: 'bg-emerald-50 border-emerald-200',
    tone: 'success',
  },
  deviation_justified: {
    label: 'Deviation Justified',
    // Cyan (not amber) so it stays distinct from `inconclusive` below, which
    // is amber — and consistent with the live CamComparisonGrid's cyan.
    icon: <ShieldCheck className="w-4 h-4 text-cyan-700" />,
    cls: 'bg-cyan-50 border-cyan-200',
    tone: 'info',
  },
  violated: {
    label: 'Failed',
    icon: <XCircle className="w-4 h-4 text-red-700" />,
    cls: 'bg-red-50 border-red-200',
    tone: 'danger',
  },
  inconclusive: {
    label: 'Inconclusive',
    icon: <MinusCircle className="w-4 h-4 text-amber-700" />,
    cls: 'bg-amber-50 border-amber-200',
    tone: 'warning',
  },
  no_los: {
    label: 'No LOS Data',
    icon: <MinusCircle className="w-4 h-4 text-slate-500" />,
    cls: 'bg-slate-50 border-slate-200',
    tone: 'neutral',
  },
};

function VerdictHeader({
  status, finding,
}: { status: RowStatus; finding: FindingDTO | null }) {
  const meta = STATUS_META[status];
  return (
    <div className={`rounded-lg border ${meta.cls} p-3 mb-4`}>
      <div className="flex items-center gap-2 mb-1.5">
        {meta.icon}
        <span className="text-label font-semibold text-text-primary">
          {meta.label}
        </span>
        {finding?.ruleId && (
          <code className="text-micro text-text-tertiary ml-auto font-mono">
            {finding.ruleId}
          </code>
        )}
      </div>
      {finding?.description && (
        <div className="text-caption text-text-secondary leading-snug">
          {finding.description}
        </div>
      )}
    </div>
  );
}

// -------------------------------------------------------------------------
// Small shared building blocks
// -------------------------------------------------------------------------

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="mb-4">
      <div className="text-micro uppercase tracking-wide font-semibold text-text-tertiary mb-1.5">
        {title}
      </div>
      {children}
    </div>
  );
}

function KvRow({ k, v, mono = false }: { k: string; v: ReactNode; mono?: boolean }) {
  return (
    <div className="flex items-start gap-3 py-1 border-b border-glass-hairline last:border-0">
      <div className="text-caption text-text-tertiary min-w-[110px] shrink-0">{k}</div>
      <div className={`text-caption text-text-primary ${mono ? 'font-mono' : ''}`}>
        {v ?? <span className="text-text-tertiary">—</span>}
      </div>
    </div>
  );
}

function StatusPill({ ok, label }: { ok: boolean; label?: string }) {
  return ok ? (
    <Badge tone="success">{label ?? '✓'}</Badge>
  ) : (
    <Badge tone="danger">{label ?? '✗'}</Badge>
  );
}

function DeviationCard({ deviation }: { deviation: {
  deviationType: string;
  status: string;
  approverId?: string | null;
  approverRemarks?: string | null;
  mitigant?: string | null;
} }) {
  return (
    <div className="rounded-md border border-cyan-200 bg-cyan-50/60 p-3">
      <div className="flex items-center gap-2 mb-1.5">
        <ShieldCheck className="w-3.5 h-3.5 text-cyan-700" />
        <span className="text-caption font-semibold text-cyan-900">
          Approved deviation
        </span>
        <code className="text-micro font-mono text-cyan-700 ml-auto">
          {deviation.deviationType}
        </code>
      </div>
      <div className="space-y-1 text-caption">
        <KvRow k="Status" v={<Badge tone="success">{deviation.status}</Badge>} />
        <KvRow k="Approver" v={deviation.approverId ?? '—'} />
        {deviation.approverRemarks && (
          <KvRow k="Remarks" v={deviation.approverRemarks} />
        )}
        {deviation.mitigant && (
          <KvRow k="Mitigant" v={deviation.mitigant} />
        )}
      </div>
    </div>
  );
}

function EmptyHint({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-md border border-dashed border-glass-hairline bg-slate-50/60 p-3 text-caption text-text-tertiary text-center">
      {children}
    </div>
  );
}

/**
 * PdfDocumentsList — list of documents with click-to-expand inline preview.
 *
 * Used in evidence renderers that have multiple related PDFs (Point 1 —
 * documents required; Point 17 — KFS + Loan Agreement). The auditor
 * clicks a row to expand the PDF inline; only one is open at a time to
 * keep the drawer compact.
 *
 * `expandedDocumentType` and `expandedPage` let parent components
 * programmatically open a specific document at a specific page (used by
 * Point 11 when the auditor clicks "→ Page N" on a comparison field).
 */
function PdfDocumentsList({
  loanFileId, documents, types, hint, autoExpand = false,
  expandedDocumentType = null, expandedPage = null,
}: {
  loanFileId: string;
  documents: EvidenceDrawerProps['documents'];
  types: string[];
  hint?: string;
  /** If true and only 1 doc matches, auto-expand it. */
  autoExpand?: boolean;
  /** If set, auto-expand the first matching document of this type. */
  expandedDocumentType?: string | null;
  /** Page to open the auto-expanded document on. */
  expandedPage?: number | null;
}) {
  const matched = documents.filter((d) => types.includes(d.documentType));

  // Pre-compute initial expansion: explicit type request > autoExpand > none.
  const explicitMatch = expandedDocumentType
    ? matched.find((d) => d.documentType === expandedDocumentType)
    : null;
  const initialExpand =
    explicitMatch?.id ??
    (autoExpand && matched.length === 1 ? matched[0]!.id : null);

  const [expandedId, setExpandedId] = useState<string | null>(initialExpand);

  // When the parent changes expandedDocumentType (e.g. auditor clicks
  // "→ Page N" on a comparison field), re-open the matching document.
  // Runs as a post-render effect to avoid setState-during-render flicker.
  const desiredId = explicitMatch?.id ?? null;
  useEffect(() => {
    if (desiredId) setExpandedId(desiredId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedDocumentType]);

  if (matched.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-glass-hairline bg-slate-50/60 p-3 text-caption text-text-tertiary text-center">
        No matching PDF on this loan file.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {hint && (
        <div className="text-micro text-text-tertiary">{hint}</div>
      )}
      {matched.map((d) => {
        const isOpen = expandedId === d.id;
        // Only apply the requested page to the currently-expanded doc.
        const pageForThisDoc =
          isOpen && d.documentType === expandedDocumentType
            ? expandedPage
            : null;
        return (
          <div key={d.id} className="rounded-md border border-glass-hairline overflow-hidden">
            <button
              type="button"
              onClick={() => setExpandedId(isOpen ? null : d.id)}
              className="w-full flex items-center gap-2 px-3 py-2 text-left bg-slate-50/60 hover:bg-slate-100/60 transition-colors"
              aria-expanded={isOpen}
            >
              <FileText className="w-3.5 h-3.5 text-text-tertiary shrink-0" />
              <span className="text-caption font-medium text-text-primary flex-1 min-w-0 truncate">
                {d.originalFileName}
              </span>
              <Badge tone="muted" className="text-micro">{d.documentType}</Badge>
              {pageForThisDoc && (
                <Badge tone="info" className="text-micro">→ p.{pageForThisDoc}</Badge>
              )}
              <span className="text-micro text-brand-blue ml-1">
                {isOpen ? '− Hide' : '+ Preview'}
              </span>
            </button>
            {isOpen && (
              <div className="p-2 bg-white">
                <PdfEmbed
                  loanFileId={loanFileId}
                  documentId={d.id}
                  fileName={d.originalFileName}
                  initialPage={pageForThisDoc}
                  heightPx={420}
                  // Force re-mount when page changes so iframe reloads
                  key={`${d.id}-p${pageForThisDoc ?? 0}`}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Need useState import — added at top with ReactNode

// -------------------------------------------------------------------------
// MAIN COMPONENT
// -------------------------------------------------------------------------

export function EvidenceDrawer(props: EvidenceDrawerProps) {
  const { open, onClose, point, label } = props;

  return (
    <Sheet
      open={open}
      onOpenChange={(v) => { if (!v) onClose(); }}
      side="right"
      title={point ? `Point ${point}: ${label}` : 'Evidence'}
      widthPx={560}
    >
      <VerdictHeader
        status={props.status}
        finding={props.finding}
      />

      {point && renderEvidenceForPoint(point, props)}
    </Sheet>
  );
}

// -------------------------------------------------------------------------
// Dispatcher — picks the right renderer for each checklist point
// -------------------------------------------------------------------------

function renderEvidenceForPoint(
  point: number,
  props: EvidenceDrawerProps,
): ReactNode {
  switch (point) {
    case 1:  return <DocumentsRequired {...props} />;
    case 2:  return <ApiStatusDetails {...props} />;
    case 3:  return <DedupeScreening {...props} />;
    case 4:  return <CibilScore {...props} />;
    case 5:  return <HunterVerification {...props} />;
    case 6:  return <BqsCheck {...props} />;
    case 7:  return <RcuStatus {...props} />;
    case 8:
    case 9:
    case 10: return <IncomeProof {...props} point={point} />;
    case 11: return <LoanEligibility {...props} />;
    case 12: return <CompanyCap {...props} />;
    case 13: return <DeviationMatrix {...props} />;
    case 14: return <ValidityOfSanction {...props} />;
    case 15: return <SanctionConditions {...props} />;
    case 16: return <EAgreement {...props} />;
    case 17: return <KfsAttached {...props} />;
    default:
      return <EmptyHint>No evidence renderer for point {point}.</EmptyHint>;
  }
}

// -------------------------------------------------------------------------
// "LOS data missing" fallback — many renderers need LOS data; this is the
// shared empty state when none is attached.
// -------------------------------------------------------------------------

/**
 * NotReported — the LOS payload was uploaded, but the real LOS API doesn't
 * carry this section at all. Distinct from a failed check: nothing was
 * verified, so we must not render a pass/fail or a substituted value.
 */
function NotReported({ what }: { what: string }) {
  return (
    <EmptyHint>
      <div className="flex items-center justify-center gap-1.5 mb-1">
        <MinusCircle className="w-3.5 h-3.5" />
        <span className="font-medium">Not reported by LOS</span>
      </div>
      {what} is absent from the LOS payload. This check could not be evaluated —
      it is neither a pass nor a fail.
    </EmptyHint>
  );
}

function NoLosFallback() {
  return (
    <EmptyHint>
      <div className="flex items-center justify-center gap-1.5 mb-1">
        <MinusCircle className="w-3.5 h-3.5" />
        <span className="font-medium">LOS data not uploaded</span>
      </div>
      This check reads from LOS workflow data. Upload the LOS JSON via the
      LOS Data card to evaluate it.
    </EmptyHint>
  );
}

// =========================================================================
// POINT 1 — Documents Required (Type A — PDF evidence)
// =========================================================================

function DocumentsRequired(props: EvidenceDrawerProps) {
  const { documents, losData, loanFileId } = props;
  const productCode = losData?.data?.productCode ?? '—';

  // Group documents by type
  const byType = new Map<string, EvidenceDrawerProps['documents']>();
  for (const d of documents) {
    if (!byType.has(d.documentType)) byType.set(d.documentType, []);
    byType.get(d.documentType)!.push(d);
  }

  // Expected document types (illustrative — actual list comes from the
  // backend's document_checklist masters in production).
  const expectedTypes: Array<{ type: string; label: string }> = [
    { type: 'sanction_letter', label: 'Sanction Letter' },
    { type: 'kfs',             label: 'Key Fact Statement' },
    { type: 'loan_agreement',  label: 'Loan Agreement' },
    { type: 'cam',             label: 'Credit Appraisal Memo' },
    { type: 'kyc',             label: 'KYC documents' },
    { type: 'salary_slip',     label: 'Salary slips / income proof' },
  ];

  return (
    <>
      <Section title="Required documents">
        <ul className="space-y-1.5">
          {expectedTypes.map(({ type, label }) => {
            const found = byType.get(type) ?? [];
            return (
              <li key={type} className="flex items-start gap-2 text-caption">
                {found.length > 0
                  ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700 mt-0.5 shrink-0" />
                  : <XCircle className="w-3.5 h-3.5 text-red-700 mt-0.5 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-text-primary">
                    {label}
                    {found.length > 1 && (
                      <span className="text-text-tertiary font-normal"> ({found.length})</span>
                    )}
                  </div>
                  {found.length > 0 ? (
                    <div className="text-micro text-text-tertiary truncate">
                      {found.map((d) => d.originalFileName).join(', ')}
                    </div>
                  ) : (
                    <div className="text-micro text-red-700">Missing</div>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </Section>

      {losData && (
        <Section title="Per-applicant KYC count (from LOS)">
          <table className="w-full text-caption">
            <thead>
              <tr className="text-text-tertiary border-b border-glass-hairline">
                <th className="text-left font-medium pb-1">Applicant</th>
                <th className="text-left font-medium pb-1">Role</th>
                <th className="text-right font-medium pb-1">KYC docs</th>
              </tr>
            </thead>
            <tbody>
              {losData.data.applicants.map((a) => {
                const count = a.kycDocuments?.length ?? 0;
                return (
                  <tr key={a.applicantId} className="border-b border-glass-hairline last:border-0">
                    <td className="py-1.5">{a.name}</td>
                    <td className="py-1.5 text-text-tertiary">{a.role}</td>
                    <td className="py-1.5 text-right">
                      <span className={count >= 2 ? 'text-emerald-700' : 'text-red-700'}>
                        {count} {count >= 2 ? '✓' : '✗'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="text-micro text-text-tertiary mt-1.5">
            Policy: minimum 2 KYC documents per applicant. Product: {productCode}
          </div>
        </Section>
      )}

      <Section title="PDF evidence">
        <PdfDocumentsList
          loanFileId={loanFileId}
          documents={documents}
          types={['kyc', 'cam', 'sanction_letter', 'kfs', 'loan_agreement', 'salary_slip', 'bank_statement']}
          hint="Click a document below to expand and preview inline."
        />
      </Section>
    </>
  );
}

// =========================================================================
// POINT 2 — API Status Details
// =========================================================================

function ApiStatusDetails({ losData }: EvidenceDrawerProps) {
  if (!losData) return <NoLosFallback />;
  const api = losData.data.apiStatusDetails;
  if (!api) return <NotReported what="API status details" />;
  const checks = [
    { name: 'PAN Card',     entry: api.panCard },
    { name: 'Aadhar',       entry: api.aadhar },
    { name: 'eMandate',     entry: api.eMandate },
    { name: 'CKYC',         entry: api.ckyc },
    { name: 'PAN Profile',  entry: api.panProfile },
  ];
  return (
    <Section title="LOS API verification statuses">
      <div className="overflow-x-auto">
      <table className="w-full text-caption">
        <thead>
          <tr className="text-text-tertiary border-b border-glass-hairline">
            <th className="text-left font-medium pb-1.5">API</th>
            <th className="text-left font-medium pb-1.5">Status</th>
            <th className="text-right font-medium pb-1.5">Validated</th>
          </tr>
        </thead>
        <tbody>
          {checks.map(({ name, entry }) => {
            const ok = entry?.status === 'Success' && entry.validated === true;
            return (
              <tr key={name} className="border-b border-glass-hairline last:border-0">
                <td className="py-1.5 font-medium">{name}</td>
                <td className="py-1.5">
                  {entry
                    ? <Badge tone={ok ? 'success' : 'danger'}>{entry.status}</Badge>
                    : <span className="text-text-tertiary">Not reported by LOS</span>}
                </td>
                <td className="py-1.5 text-right">
                  {entry
                    ? <StatusPill ok={!!entry.validated} />
                    : <span className="text-text-tertiary">—</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
      <div className="text-micro text-text-tertiary mt-2">
        Policy: all 5 APIs must return Success AND validated=true.
      </div>
    </Section>
  );
}

// =========================================================================
// POINT 3 — Dedupe / Posidex
// =========================================================================

function DedupeScreening({ losData }: EvidenceDrawerProps) {
  if (!losData) return <NoLosFallback />;
  const apps = losData.data.applicants;
  const deviation = losData.data.deviations.find(
    (d) => d.deviationType === 'dedupe_pending' && d.status === 'approved',
  );

  return (
    <>
      <Section title="Per-applicant Posidex status">
        <table className="w-full text-caption">
          <thead>
            <tr className="text-text-tertiary border-b border-glass-hairline">
              <th className="text-left font-medium pb-1.5">Applicant</th>
              <th className="text-left font-medium pb-1.5">Role</th>
              <th className="text-left font-medium pb-1.5">PAN</th>
              <th className="text-right font-medium pb-1.5">Status</th>
            </tr>
          </thead>
          <tbody>
            {apps.map((a) => {
              const ok = a.posidexStatus === 'Validated';
              return (
                <tr key={a.applicantId} className="border-b border-glass-hairline last:border-0">
                  <td className="py-1.5 font-medium">{a.name}</td>
                  <td className="py-1.5 text-text-tertiary">{a.role}</td>
                  <td className="py-1.5 font-mono text-micro">{a.pan ?? '—'}</td>
                  <td className="py-1.5 text-right">
                    <Badge tone={ok ? 'success' : 'danger'}>{a.posidexStatus}</Badge>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="text-micro text-text-tertiary mt-2">
          Policy: every applicant's Posidex must be Validated before disbursement.
        </div>
      </Section>

      {deviation && (
        <Section title="Justifying deviation">
          <DeviationCard deviation={deviation} />
        </Section>
      )}
    </>
  );
}

// =========================================================================
// POINT 4 — CIBIL
// =========================================================================

function CibilScore({ losData, finding }: EvidenceDrawerProps) {
  if (!losData) return <NoLosFallback />;
  const productCode = losData.data.productCode;
  const apps = losData.data.applicants;
  const deviation = losData.data.deviations.find(
    (d) => d.deviationType === 'cibil_below_threshold' && d.status === 'approved',
  );
  // Threshold info is in the finding's evidence.extra if available
  const extra = (finding?.evidence as unknown as { extra?: { threshold?: { minScore: number; operator: string } } } | undefined)?.extra;
  const threshold = extra?.threshold;

  return (
    <>
      {threshold && (
        <Section title="Threshold (from masters)">
          <div className="rounded-md border border-glass-hairline bg-slate-50/60 p-2.5">
            <KvRow k="Product" v={productCode} />
            <KvRow
              k="Required CIBIL"
              v={`${threshold.operator === 'gte' ? '≥' : '≤'} ${threshold.minScore}`}
            />
          </div>
        </Section>
      )}

      <Section title="Per-applicant CIBIL scores">
        <div className="overflow-x-auto">
        <table className="w-full text-caption">
          <thead>
            <tr className="text-text-tertiary border-b border-glass-hairline">
              <th className="text-left font-medium pb-1.5">Applicant</th>
              <th className="text-left font-medium pb-1.5">Role</th>
              <th className="text-right font-medium pb-1.5">CIBIL</th>
              <th className="text-right font-medium pb-1.5">Meets?</th>
            </tr>
          </thead>
          <tbody>
            {apps.map((a) => {
              const score = a.cibilScore;
              const meets = threshold && score !== null
                ? (threshold.operator === 'gte'
                  ? score >= threshold.minScore
                  : score <= threshold.minScore)
                : null;
              return (
                <tr key={a.applicantId} className="border-b border-glass-hairline last:border-0">
                  <td className="py-1.5 font-medium">{a.name}</td>
                  <td className="py-1.5 text-text-tertiary">{a.role}</td>
                  <td className="py-1.5 text-right font-mono">{score ?? '—'}</td>
                  <td className="py-1.5 text-right">
                    {meets === null
                      ? <span className="text-text-tertiary">—</span>
                      : <StatusPill ok={meets} />}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </Section>

      {deviation && (
        <Section title="Justifying deviation">
          <DeviationCard deviation={deviation} />
        </Section>
      )}
    </>
  );
}

// =========================================================================
// POINT 5 — Hunter
// =========================================================================

function HunterVerification({ losData }: EvidenceDrawerProps) {
  if (!losData) return <NoLosFallback />;
  const h = losData.data.hunterVerification;
  const deviation = losData.data.deviations.find(
    (d) => d.deviationType === 'hunter_negative' && d.status === 'approved',
  );
  return (
    <>
      <Section title="Hunter verification result">
        {h ? (
          <div className="rounded-md border border-glass-hairline bg-slate-50/60 p-3 space-y-1">
            <KvRow
              k="Result"
              v={<Badge tone={h.result === 'Clean' ? 'success' : 'danger'}>{h.result}</Badge>}
            />
            <KvRow k="Match found" v={h.matchFound ? 'Yes' : 'No'} />
            {h.errorDescription && (
              <KvRow k="Error description" v={h.errorDescription} />
            )}
          </div>
        ) : (
          <NotReported what="Hunter verification" />
        )}
      </Section>

      {deviation && (
        <Section title="Justifying deviation">
          <DeviationCard deviation={deviation} />
        </Section>
      )}

      <Section title="Rule logic">
        <div className="text-caption text-text-secondary bg-slate-50/40 border border-glass-hairline rounded-md p-2.5">
          Policy: Hunter result must be <span className="font-mono">"Clean"</span> with{' '}
          <span className="font-mono">matchFound=false</span>. A
          <span className="font-mono"> "Negative" </span>
          result is acceptable only if an approved <span className="font-mono">hunter_negative</span> deviation is on file with a documented mitigant.
        </div>
      </Section>
    </>
  );
}

// =========================================================================
// POINT 6 — BQS
// =========================================================================

function BqsCheck({ losData }: EvidenceDrawerProps) {
  if (!losData) return <NoLosFallback />;
  const b = losData.data.bqsCheck;
  const deviation = losData.data.deviations.find(
    (d) => d.deviationType === 'bqs_negative' && d.status === 'approved',
  );
  return (
    <>
      <Section title="BQS check result">
        {b ? (
          <div className="rounded-md border border-glass-hairline bg-slate-50/60 p-3 space-y-1">
            <KvRow
              k="Result"
              v={<Badge tone={b.result === 'No Match' ? 'success' : 'danger'}>{b.result}</Badge>}
            />
            {b.matchDetails && <KvRow k="Match details" v={b.matchDetails} />}
          </div>
        ) : (
          <NotReported what="BQS check" />
        )}
      </Section>
      {deviation && (
        <Section title="Justifying deviation">
          <DeviationCard deviation={deviation} />
        </Section>
      )}
    </>
  );
}

// =========================================================================
// POINT 7 — RCU
// =========================================================================

function RcuStatus({ losData }: EvidenceDrawerProps) {
  if (!losData) return <NoLosFallback />;
  const r = losData.data.rcuVerification;
  const deviation = losData.data.deviations.find(
    (d) => d.deviationType === 'rcu_negative' && d.status === 'approved',
  );
  return (
    <>
      <Section title="RCU verification">
        {r ? (
          <div className="rounded-md border border-glass-hairline bg-slate-50/60 p-3 space-y-1">
            <KvRow k="Type" v={r.type} />
            <KvRow
              k="Outcome"
              v={<Badge tone={r.outcome === 'Positive' ? 'success' : r.outcome === 'Pending' ? 'warning' : 'danger'}>{r.outcome}</Badge>}
            />
            {r.status && <KvRow k="Status" v={r.status} />}
            {r.remarks && <KvRow k="Remarks" v={r.remarks} />}
          </div>
        ) : (
          <NotReported what="RCU verification" />
        )}
      </Section>
      {deviation && (
        <Section title="Justifying deviation">
          <DeviationCard deviation={deviation} />
        </Section>
      )}
    </>
  );
}

// =========================================================================
// POINTS 8, 9, 10 — Income Proof / Novel Check
// =========================================================================

function IncomeProof({ point, losData, bankingVerification }: Omit<EvidenceDrawerProps, 'point'> & { point: number }) {
  if (!losData) return <NoLosFallback />;
  const nc = losData.data.novelCheckBanking;
  const cfa = losData.data.cashFlowAnalysis;
  const bv = bankingVerification?.status === 'completed' ? bankingVerification.response : null;
  return (
    <>
      {/* ---- Real banking verification data (Chunk 2) ---- */}
      {bv && (
        <Section title="Novel Check — Verified (Independent Analysis)">
          <div className="rounded-md border border-emerald-200 bg-emerald-50/50 p-3 space-y-1.5 mb-1">
            <div className="text-micro font-semibold uppercase tracking-wide text-emerald-700 mb-1.5">
              Independent bank statement analysis · Provider: {bankingVerification!.provider}
            </div>
            <KvRow
              k="Salary credits found"
              v={<StatusPill ok={bv.salaryCreditsFound > 0} label={String(bv.salaryCreditsFound)} />}
            />
            <KvRow k="Months covered" v={String(bv.monthsCovered)} />
            <KvRow
              k="Monthly avg balance"
              v={`₹${bv.monthlyAverageBalanceInr.toLocaleString('en-IN')}`}
            />
            <KvRow
              k="Employer match"
              v={<StatusPill ok={bv.salaryEmployerMatch} label={bv.salaryEmployerMatch ? `Yes (${Math.round(bv.salaryEmployerMatchConfidence * 100)}% confidence)` : 'No match'} />}
            />
            {bv.observedSalaryAvgInr !== null && bv.observedSalaryAvgInr !== undefined && (
              <KvRow
                k="Observed salary avg"
                v={`₹${bv.observedSalaryAvgInr.toLocaleString('en-IN')}`}
              />
            )}
            {bv.editedPdfDetected && (
              <KvRow
                k="PDF tamper flag"
                v={<StatusPill ok={false} label="Editing detected" />}
              />
            )}
            {bv.anomalies.length > 0 && (
              <div className="mt-1.5 text-caption text-amber-800">
                ⚠ {bv.anomalies.length} anomaly flag(s): {bv.anomalies.join('; ')}
              </div>
            )}
          </div>
        </Section>
      )}

      {/* ---- LOS-declared Novel Check flags (always shown) ---- */}
      <Section title={bv ? 'LOS-Declared Novel Check (self-reported)' : 'Novel Check (banking analysis)'}>
        {nc ? (
          <div className="rounded-md border border-glass-hairline bg-slate-50/60 p-3 space-y-1">
            <KvRow
              k="Performed"
              v={<StatusPill ok={nc.performed} label={nc.performed ? 'Yes' : 'No'} />}
            />
            <KvRow
              k="Salary credits verified"
              v={
                nc.salaryCreditsVerified === null || nc.salaryCreditsVerified === undefined
                  ? '—'
                  : <StatusPill ok={!!nc.salaryCreditsVerified} label={nc.salaryCreditsVerified ? 'Yes' : 'No'} />
              }
            />
            {nc.monthlyAverageBalance !== null && nc.monthlyAverageBalance !== undefined && (
              <KvRow
                k="Monthly avg balance"
                v={`₹${nc.monthlyAverageBalance.toLocaleString('en-IN')}`}
              />
            )}
            {nc.salaryEntries !== null && nc.salaryEntries !== undefined && (
              <KvRow k="Salary entries" v={nc.salaryEntries} />
            )}
          </div>
        ) : (
          <NotReported what="LOS Novel Check banking data" />
        )}
        {!bv && (
          <div className="mt-2 text-micro text-text-tertiary">
            Upload a bank statement PDF and run Novel Check verification to see independently
            verified data alongside LOS-declared figures.
          </div>
        )}
      </Section>

      <Section title="Cash Flow Analysis">
        {cfa ? (
          <div className="rounded-md border border-glass-hairline bg-slate-50/60 p-3 space-y-1">
            <KvRow k="Assessment method" v={cfa.assessmentMethod ?? '—'} />
            <KvRow
              k="CFA completed"
              v={<StatusPill ok={!!cfa.cfaCompleted} label={cfa.cfaCompleted ? 'Yes' : 'No'} />}
            />
          </div>
        ) : (
          <NotReported what="Cash flow analysis" />
        )}
      </Section>

      <Section title="Why this check?">
        <div className="text-caption text-text-secondary bg-slate-50/40 border border-glass-hairline rounded-md p-2.5">
          {point === 8 && 'Personal Loan and Business Loan require salary credits verified via Novel Check banking analysis.'}
          {point === 9 && 'Machine Loan and Home Loan use Banking Surrogate / GST Program / CFA for income assessment.'}
          {point === 10 && 'Standalone check confirming Novel Check banking analysis was executed.'}
        </div>
      </Section>
    </>
  );
}

// =========================================================================
// POINT 11 — Loan Eligibility (Mixed — CAM PDF + LOS data)
// =========================================================================

function LoanEligibility({ losData, finding, documents, loanFileId, parsingView }: EvidenceDrawerProps) {
  // Hook called unconditionally (rules of hooks). State holds the page
  // the user wants the embedded CAM PDF to jump to. null = use auto-detected
  // first page from the extraction; non-null overrides.
  const [requestedPage, setRequestedPage] = useState<number | null>(null);

  if (!losData) return <NoLosFallback />;
  const ld = losData.data.loanDetails;
  // The rule populates evidence.extra.mismatches when there's a disagreement.
  const extra = (finding?.evidence as unknown as { extra?: { mismatches?: Array<{ field: string; los: number; cam: number; tolerance: number }> } } | undefined)?.extra;
  const mismatches = extra?.mismatches ?? [];
  const mismatchMap = new Map(mismatches.map((m) => [m.field, m]));

  // Find the CAM document and look up the page number for each field
  // from the parsed extraction. Field names in the schema are typically
  // sanctioned_amount, interest_rate, tenure_months, emi. Names vary
  // across schemas — we try a few common spellings per row.
  const camDoc = documents.find((d) => d.documentType === 'cam');
  const camSnapshot = parsingView?.documents.find(
    (d) => d.documentId === camDoc?.id,
  );
  const extractedFields = camSnapshot?.extraction?.fields ?? null;

  const fieldToPage = (...candidates: string[]): number | null => {
    if (!extractedFields) return null;
    for (const name of candidates) {
      const f = extractedFields[name];
      if (f?.source?.pageNumber && f.source.pageNumber > 0) {
        return f.source.pageNumber;
      }
    }
    return null;
  };

  type Row = {
    field: string;
    label: string;
    los: number | null;
    cam: number | null;
    page: number | null;
  };
  const rows: Row[] = [
    {
      field: 'sanctioned_amount', label: 'Sanctioned amount',
      los: ld.sanctionedAmount,
      cam: mismatchMap.get('sanctioned_amount')?.cam ?? null,
      page: fieldToPage('sanctioned_amount', 'loan_amount', 'amount'),
    },
    {
      field: 'interest_rate_pct', label: 'Interest rate',
      los: ld.interestRatePct,
      cam: mismatchMap.get('interest_rate_pct')?.cam ?? null,
      page: fieldToPage('interest_rate', 'interest_rate_pct', 'roi'),
    },
    {
      field: 'tenure_months', label: 'Tenure (months)',
      los: ld.tenureMonths,
      cam: mismatchMap.get('tenure_months')?.cam ?? null,
      page: fieldToPage('tenure_months', 'tenure'),
    },
    {
      field: 'emi', label: 'EMI',
      los: ld.emiAmount,
      cam: mismatchMap.get('emi')?.cam ?? null,
      page: fieldToPage('emi', 'emi_amount'),
    },
  ];

  const fmt = (v: number | null, field: string) => {
    if (v === null) return '—';
    if (field === 'interest_rate_pct') return `${v}%`;
    if (field === 'tenure_months') return String(v);
    return `₹${v.toLocaleString('en-IN')}`;
  };

  // The "default page" for the auto-expanded CAM PDF — use the first
  // available page from the four fields, so the embed lands on a useful page.
  const defaultPage = rows.map((r) => r.page).find((p): p is number => p !== null) ?? null;
  const effectivePage = requestedPage ?? defaultPage;

  return (
    <>
      <Section title="LOS vs CAM PDF — side-by-side">
        <div className="overflow-x-auto">
        <table className="w-full text-caption">
          <thead>
            <tr className="text-text-tertiary border-b border-glass-hairline">
              <th className="text-left font-medium pb-1.5">Field</th>
              <th className="text-right font-medium pb-1.5">LOS</th>
              <th className="text-right font-medium pb-1.5">CAM PDF</th>
              <th className="text-right font-medium pb-1.5">Match?</th>
              <th className="text-right font-medium pb-1.5 pl-2">Show</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const m = mismatchMap.get(r.field);
              const isActive = requestedPage !== null && requestedPage === r.page;
              return (
                <tr key={r.field} className="border-b border-glass-hairline last:border-0">
                  <td className="py-1.5">{r.label}</td>
                  <td className="py-1.5 text-right font-mono">{fmt(r.los, r.field)}</td>
                  <td className="py-1.5 text-right font-mono">{m ? fmt(m.cam, r.field) : '—'}</td>
                  <td className="py-1.5 text-right">
                    {m ? <StatusPill ok={false} /> : <StatusPill ok={true} />}
                  </td>
                  <td className="py-1.5 text-right pl-2">
                    {r.page ? (
                      <button
                        type="button"
                        onClick={() => setRequestedPage(r.page)}
                        className={`inline-flex items-center gap-1 text-micro font-medium px-2 py-0.5 rounded-md transition-colors ${
                          isActive
                            ? 'bg-brand-blue text-white'
                            : 'text-brand-blue hover:bg-brand-blue/[0.06]'
                        }`}
                        title={`Jump CAM PDF to page ${r.page}`}
                      >
                        → p.{r.page}
                      </button>
                    ) : (
                      <span className="text-micro text-text-tertiary">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
        <div className="text-micro text-text-tertiary mt-2">
          Tolerances: amount ±₹100 · rate ±0.01% · tenure ±0 months · EMI ±₹10
          {!extractedFields && (
            <span className="block mt-1 text-amber-700">
              Page-number data not available — CAM may not be parsed yet. Re-run parsing to enable jump-to-page.
            </span>
          )}
        </div>
      </Section>

      <Section title="CAM PDF (source of truth)">
        <PdfDocumentsList
          loanFileId={loanFileId}
          documents={documents}
          types={['cam']}
          hint={
            effectivePage
              ? `Showing CAM at page ${effectivePage}. Click "→ p.N" above to jump to a different field.`
              : 'Compare the LOS values above against the values stated in the CAM. The CAM is the source of truth for loan eligibility.'
          }
          autoExpand={true}
          expandedDocumentType="cam"
          expandedPage={effectivePage}
        />
      </Section>
    </>
  );
}

// =========================================================================
// POINT 12 — Company Cap
// =========================================================================

function CompanyCap({ losData, finding }: EvidenceDrawerProps) {
  if (!losData) return <NoLosFallback />;
  const cc = losData.data.companyCategorization;
  const sanctioned = losData.data.loanDetails.sanctionedAmount;
  const extra = (finding?.evidence as unknown as { extra?: { effectiveCap?: number } } | undefined)?.extra;
  const effectiveCap = extra?.effectiveCap ?? cc?.maxLoanCapForCategory ?? null;
  const deviation = losData.data.deviations.find(
    (d) => d.deviationType === 'company_cap_exceeded' && d.status === 'approved',
  );
  // Both sides must be present; an absent value is not a pass.
  const capCheck =
    sanctioned !== null && effectiveCap !== null
      ? { within: sanctioned <= effectiveCap, overBy: sanctioned - effectiveCap }
      : null;

  return (
    <>
      <Section title="Company categorization">
        {cc ? (
          <div className="rounded-md border border-glass-hairline bg-slate-50/60 p-3 space-y-1">
            <KvRow k="Employer" v={cc.employerName} />
            <KvRow k="Category" v={<Badge tone="info">{cc.category}</Badge>} />
            <KvRow k="Max cap" v={effectiveCap !== null ? `₹${effectiveCap.toLocaleString('en-IN')}` : '—'} />
          </div>
        ) : (
          <NotReported what="Company categorization" />
        )}
      </Section>

      <Section title="Sanctioned vs cap">
        <div className="rounded-md border border-glass-hairline bg-slate-50/60 p-3 space-y-1">
          <KvRow k="Sanctioned amount" v={sanctioned !== null ? `₹${sanctioned.toLocaleString('en-IN')}` : 'Not reported by LOS'} />
          <KvRow k="Cap" v={effectiveCap !== null ? `₹${effectiveCap.toLocaleString('en-IN')}` : 'Not reported by LOS'} />
          <KvRow
            k="Within cap"
            v={
              capCheck === null
                ? <span className="text-text-tertiary">Not evaluated — value not reported by LOS</span>
                : <StatusPill ok={capCheck.within} label={capCheck.within ? 'Yes' : `Over by ₹${capCheck.overBy.toLocaleString('en-IN')}`} />
            }
          />
        </div>
      </Section>

      {deviation && (
        <Section title="Justifying deviation">
          <DeviationCard deviation={deviation} />
        </Section>
      )}
    </>
  );
}

// =========================================================================
// POINT 13 — Deviation Matrix
// =========================================================================

function DeviationMatrix({ losData }: EvidenceDrawerProps) {
  if (!losData) return <NoLosFallback />;
  const list = losData.data.deviations;
  if (list.length === 0) {
    return (
      <Section title="Deviations on file">
        <EmptyHint>No deviations recorded for this loan file.</EmptyHint>
      </Section>
    );
  }
  return (
    <Section title={`Deviations on file (${list.length})`}>
      <div className="space-y-2.5">
        {list.map((d, i) => (
          <div key={i} className="rounded-md border border-glass-hairline bg-slate-50/60 p-3">
            <div className="flex items-center gap-2 mb-1.5">
              <span className="text-micro font-semibold text-text-secondary">#{i + 1}</span>
              <code className="text-micro font-mono text-text-primary">{d.deviationType}</code>
              <Badge
                tone={d.status === 'approved' ? 'success' : d.status === 'pending' ? 'warning' : 'danger'}
                className="ml-auto"
              >
                {d.status}
              </Badge>
            </div>
            <div className="space-y-0.5 text-caption">
              {d.approverId && <KvRow k="Approver" v={d.approverId} />}
              {d.approverRemarks && <KvRow k="Remarks" v={d.approverRemarks} />}
              {d.mitigant
                ? <KvRow k="Mitigant" v={d.mitigant} />
                : <KvRow k="Mitigant" v={<span className="text-red-700 italic">missing</span>} />
              }
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
}

// =========================================================================
// POINT 14 — Validity of Sanction
// =========================================================================

function ValidityOfSanction({ losData, finding }: EvidenceDrawerProps) {
  if (!losData) return <NoLosFallback />;
  const d = losData.data.dates;
  const deviation = losData.data.deviations.find(
    (dv) => dv.deviationType === 'validity_exceeded' && dv.status === 'approved',
  );
  const extra = (finding?.evidence as unknown as { extra?: { gap?: number; validity?: number } } | undefined)?.extra;
  // Compute gap locally as fallback when finding.evidence doesn't have it
  const computeGap = (): number | null => {
    if (!d.sanctionDate || !d.disbursementDate) return null;
    const a = new Date(d.sanctionDate).getTime();
    const b = new Date(d.disbursementDate).getTime();
    if (Number.isNaN(a) || Number.isNaN(b)) return null;
    return Math.floor((b - a) / (1000 * 60 * 60 * 24));
  };
  const gap = extra?.gap ?? computeGap();
  const validity = extra?.validity ?? 30;

  return (
    <>
      <Section title="Dates">
        <div className="rounded-md border border-glass-hairline bg-slate-50/60 p-3 space-y-1">
          <KvRow k="Application date" v={d.applicationDate ?? '—'} />
          <KvRow k="Sanction date" v={d.sanctionDate ?? '—'} />
          <KvRow k="Disbursement date" v={d.disbursementDate ?? '—'} />
        </div>
      </Section>

      <Section title="Validity calculation">
        <div className="rounded-md border border-glass-hairline bg-slate-50/60 p-3 space-y-1">
          <KvRow k="Sanction → Disbursement" v={gap === null ? '—' : `${gap} days`} />
          <KvRow k="Allowed validity" v={`${validity} days`} />
          <KvRow
            k="Within validity"
            v={
              gap === null
                ? <span className="text-text-tertiary">—</span>
                : <StatusPill ok={gap >= 0 && gap <= validity}
                    label={gap >= 0 && gap <= validity ? 'Yes' : 'No'} />
            }
          />
        </div>
      </Section>

      {deviation && (
        <Section title="Justifying deviation">
          <DeviationCard deviation={deviation} />
        </Section>
      )}
    </>
  );
}

// =========================================================================
// POINT 15 — Sanction Conditions
// =========================================================================

function SanctionConditions({ losData }: EvidenceDrawerProps) {
  if (!losData) return <NoLosFallback />;
  const conditions = losData.data.sanctionConditions ?? [];
  return (
    <Section title={`Conditions recorded in LOS (${conditions.length})`}>
      {conditions.length === 0 ? (
        <EmptyHint>
          No sanction conditions recorded. Even unconditional sanctions should
          record this explicitly per policy.
        </EmptyHint>
      ) : (
        <ol className="space-y-1.5 list-decimal list-inside text-caption text-text-primary">
          {conditions.map((c, i) => (
            <li key={i} className="leading-snug">{c}</li>
          ))}
        </ol>
      )}
    </Section>
  );
}

// =========================================================================
// POINT 16 — E-Agreement
// =========================================================================

function EAgreement({ losData }: EvidenceDrawerProps) {
  if (!losData) return <NoLosFallback />;
  const e = losData.data.eAgreement;
  return (
    <Section title="E-Agreement workflow metadata">
      {e ? (
        <div className="rounded-md border border-glass-hairline bg-slate-50/60 p-3 space-y-1">
          <KvRow
            k="Interface status"
            v={<Badge tone={e.interfaceStatus === 'Success' ? 'success' : 'danger'}>{e.interfaceStatus}</Badge>}
          />
          <KvRow k="Document processed date" v={e.documentProcessedDate ?? <span className="text-red-700">missing</span>} />
          <KvRow k="Signed document status" v={e.signedDocumentStatus ?? <span className="text-red-700">missing</span>} />
          <KvRow
            k="Legal document ID"
            v={e.legalDocId ? <code className="text-micro font-mono">{e.legalDocId}</code> : <span className="text-red-700">missing</span>}
            mono
          />
          {e.signedDocumentUrl && (
            <KvRow
              k="Signed doc URL"
              v={<span className="text-micro break-all">{e.signedDocumentUrl}</span>}
            />
          )}
        </div>
      ) : (
        <NotReported what="E-Agreement metadata" />
      )}
      <div className="text-micro text-text-tertiary mt-2">
        Policy: interfaceStatus must be Success and all four fields populated.
      </div>
    </Section>
  );
}

// =========================================================================
// POINT 17 — KFS Attached (Type A — PDF evidence)
// =========================================================================

function KfsAttached({ documents, loanFileId }: EvidenceDrawerProps) {
  const kfs = documents.filter((d) => d.documentType === 'kfs');
  const loanAgreement = documents.filter((d) => d.documentType === 'loan_agreement');
  return (
    <>
      <Section title="KFS + Loan Agreement presence">
        <ul className="space-y-2">
          <li className="flex items-start gap-2 text-caption">
            {kfs.length > 0
              ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700 mt-0.5 shrink-0" />
              : <XCircle className="w-3.5 h-3.5 text-red-700 mt-0.5 shrink-0" />}
            <div>
              <div className="font-medium">Key Fact Statement</div>
              {kfs.length > 0
                ? <div className="text-micro text-text-tertiary">{kfs.map((d) => d.originalFileName).join(', ')}</div>
                : <div className="text-micro text-red-700">Missing</div>}
            </div>
          </li>
          <li className="flex items-start gap-2 text-caption">
            {loanAgreement.length > 0
              ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700 mt-0.5 shrink-0" />
              : <XCircle className="w-3.5 h-3.5 text-red-700 mt-0.5 shrink-0" />}
            <div>
              <div className="font-medium">Loan Agreement</div>
              {loanAgreement.length > 0
                ? <div className="text-micro text-text-tertiary">{loanAgreement.map((d) => d.originalFileName).join(', ')}</div>
                : <div className="text-micro text-red-700">Missing</div>}
            </div>
          </li>
        </ul>
      </Section>

      <Section title="Policy">
        <div className="text-caption text-text-secondary bg-slate-50/40 border border-glass-hairline rounded-md p-2.5">
          RBI mandate: KFS must be present as Annex A of the Loan Agreement.
          Both documents must be in the loan file before disbursement.
        </div>
      </Section>

      <Section title="PDF evidence">
        <PdfDocumentsList
          loanFileId={loanFileId}
          documents={documents}
          types={['kfs', 'loan_agreement']}
          hint="Click either document to preview inline."
        />
      </Section>
    </>
  );
}

// Silence unused-imports
void Users; void Calendar; void Building2; void ClipboardCheck; void AlertTriangle; void ExternalLink;
